import { Agenda, AgendaItem } from '@api';
import { useModal } from '@common';
import { Button } from '@components';
import { AddAgendaItemModal, DeleteAgendaItemModal, EditAgendaItemModal } from '@modals';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import './AgendaList.scss';
import { cloneDeep } from 'lodash';
import { AgendaItemComp } from './AgendaItemComp';
import { AgendaItemDropComp } from './AgendaItemDropComp';

interface AgendaListProps {
	agenda: Agenda,
    canEditAgenda: boolean,
    onAgendaUpdated: (updatedAgenda: Agenda) => void,
}

const AgendaList = (props: AgendaListProps): JSX.Element => {
	const {
		agenda,
        onAgendaUpdated,
        canEditAgenda,
	} = props;

    const {
		isModalOpen: isAddAgendaModalOpen,
		closeModal: closeAddAgendaModal,
		openModal: openAddAgendaModal,
	} = useModal();

    const {
		isModalOpen: isDeleteAgendaModalOpen,
		closeModal: closeDeleteAgendaModal,
		openModal: openDeleteAgendaModal,
	} = useModal();

    const {
		isModalOpen: isEditAgendaModalOpen,
		closeModal: closeEditAgendaModal,
		openModal: openEditAgendaModal,
	} = useModal();

    const [selectedAgendaItem, setSeletectedAgendaItem] = useState<AgendaItem>(null);
    const [newItemIndex, setNewItemIndex] = useState<number>(null);
    const buttonRef = useRef<HTMLDivElement>(null);

    const onDeleteDialogOpen = useCallback((itemToDelete: AgendaItem) => {
        setSeletectedAgendaItem(itemToDelete);
        openDeleteAgendaModal();
    }, [setSeletectedAgendaItem, openDeleteAgendaModal]);

    const onEditDialogOpen = useCallback((itemToEdit: AgendaItem) => {
        setSeletectedAgendaItem(itemToEdit);
        openEditAgendaModal();
    }, [setSeletectedAgendaItem, openEditAgendaModal]);

    const onAddDialogOpen = useCallback((index: number) => {
        setNewItemIndex(index);
        openAddAgendaModal();
    }, [setNewItemIndex, openAddAgendaModal]);

    const onAddAgendaItem = useCallback((newItem: AgendaItem, itemIndex: number) => {
        let updatedAgenda: Agenda = cloneDeep(agenda);

        if (!updatedAgenda) {
            updatedAgenda = new Agenda();
            updatedAgenda.showInSummary = true;
            updatedAgenda.items = [];
        }

        if (!updatedAgenda.items) {
            updatedAgenda.items = [];
        }

        if (itemIndex) {
            updatedAgenda.items.splice(itemIndex, 0, newItem);
        } else {
            updatedAgenda.items.push(newItem);
        }
        onAgendaUpdated(updatedAgenda);

        setTimeout(() => {
            if (buttonRef && buttonRef.current) {
                buttonRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'end',
                });
            }
        }, 50);
    }, [agenda, onAgendaUpdated]);

    const onEditAgendaItem = useCallback((editedItem: AgendaItem) => {
        let updatedAgenda: Agenda = cloneDeep(agenda);

        if (!updatedAgenda) {
            updatedAgenda = new Agenda();
            updatedAgenda.showInSummary = true;
            updatedAgenda.items = [];
        }

        if (!updatedAgenda.items) {
            updatedAgenda.items = [];
        }

        for (let i = 0; i < updatedAgenda.items.length; i++) {
            const item = updatedAgenda.items[i];
            if (item.id == editedItem.id) {
                updatedAgenda.items[i] = editedItem;
            }
        }

        onAgendaUpdated(updatedAgenda);

    }, [agenda, onAgendaUpdated]);

    

    const onDeleteAgendaItem = useCallback((itemToDelete: AgendaItem) => {
        let updatedAgenda: Agenda = cloneDeep(agenda);
        if (!updatedAgenda) {
            updatedAgenda = new Agenda();
            updatedAgenda.showInSummary = true;
            updatedAgenda.items = [];
        }

        if (!updatedAgenda.items) {
            updatedAgenda.items = [];
        }

        updatedAgenda.items = updatedAgenda.items.filter(item => item.id != itemToDelete.id);
        onAgendaUpdated(updatedAgenda);

    }, [agenda, onAgendaUpdated]);

    const onAgendaItemReorderDrop = useCallback((oldIndex, newIndex) => {
        const updatedAgenda: Agenda = cloneDeep(agenda);
        let items = updatedAgenda.items;
        const agendaItem = items[oldIndex];

        if (newIndex < oldIndex) {
            items = items.filter((_, index) => index != oldIndex);
            items.splice(newIndex, 0, agendaItem);
        } else {
            items.splice(newIndex, 0, agendaItem);
            items = items.filter((_, index) => index != oldIndex);
        }
        updatedAgenda.items = items;
        onAgendaUpdated(updatedAgenda);
    }, [agenda, onAgendaUpdated]);
    
    const onAgendaItemDrop = useCallback((agendaItem: AgendaItem, newIndex: number) => {
        let updatedAgenda: Agenda = cloneDeep(agenda);
        if (!updatedAgenda) {
            updatedAgenda = new Agenda();
            updatedAgenda.showInSummary = true;
            updatedAgenda.items = [];
        }

        if (!updatedAgenda.items) {
            updatedAgenda.items = [];
        }

        const items = updatedAgenda.items;
        items.splice(newIndex, 0, agendaItem);
        updatedAgenda.items = items;
        onAgendaUpdated(updatedAgenda);
    }, [agenda, onAgendaUpdated]);

    const addAgendaModalView = useMemo((): JSX.Element => {
		return isAddAgendaModalOpen && (
			<AddAgendaItemModal
				closeModal={closeAddAgendaModal}
				onSubmit={onAddAgendaItem}
                newItemIndex={newItemIndex}
			/>
		);
	}, [closeAddAgendaModal, newItemIndex, isAddAgendaModalOpen, onAddAgendaItem]);

    const editAgendaModalView = useMemo((): JSX.Element => {
		return isEditAgendaModalOpen && (
			<EditAgendaItemModal
				closeModal={closeEditAgendaModal}
				onSubmit={onEditAgendaItem}
                agendaItem={selectedAgendaItem}
			/>
		);
	}, [closeEditAgendaModal, isEditAgendaModalOpen, selectedAgendaItem, onEditAgendaItem]);
    

    const deleteAgendaModalView = useMemo((): JSX.Element => {
		return selectedAgendaItem && isDeleteAgendaModalOpen && (
			<DeleteAgendaItemModal
                item={selectedAgendaItem}
				closeModal={closeDeleteAgendaModal}
				onSubmit={onDeleteAgendaItem}
			/>
		);
	}, [closeDeleteAgendaModal, isDeleteAgendaModalOpen, selectedAgendaItem, onDeleteAgendaItem]);


    const agendaItemsView = useMemo(() => {
        const renderItems = (sourceItems: AgendaItem[]) => {
            let items = sourceItems.map((item, ix) => (
                <React.Fragment key={item.id}>
                    <AgendaItemComp
                        canEdit={canEditAgenda}
                        index={ix}
                        item={item}
                        onAddDialogOpen={onAddDialogOpen}
                        onEditDialogOpen={onEditDialogOpen}
                        onDeleteDialogOpen={onDeleteDialogOpen}
                    />
                    <AgendaItemDropComp
                        newIndex={ix+1}
                        canEdit={canEditAgenda}
                        onAgendaItemReorderDrop={onAgendaItemReorderDrop}
                        onAgendaItemDrop={onAgendaItemDrop}
                    />
                </React.Fragment>
            ));
    
            const firstItem = <AgendaItemDropComp
                key={-999999}
                newIndex={0}
                canEdit={canEditAgenda}
                onAgendaItemReorderDrop={onAgendaItemReorderDrop}
                onAgendaItemDrop={onAgendaItemDrop}
            />;
    
            items = [firstItem].concat(items);
            return items;
        }

        return renderItems(agenda ? agenda.items : [])
    }, [agenda, canEditAgenda, onAddDialogOpen, onAgendaItemReorderDrop,
        onDeleteDialogOpen, onEditDialogOpen, onAgendaItemDrop]);

    return (
        <div>
            <div className='agenda'>
                <div className='agenda-list'>
                    {agendaItemsView}
                </div>
                {canEditAgenda && <div ref={buttonRef}>
                <Button onClick={() => { onAddDialogOpen(null); }} theme="ghost">
                    + Добавить вопрос повестки
                </Button></div>}
            </div>
            {addAgendaModalView}
            {deleteAgendaModalView}
            {editAgendaModalView}
        </div>
    )
}

export {AgendaList};